import React, {useCallback, useEffect, useRef, useState} from 'react';

import {
  GoogleMap,
  Libraries,
  LoadScriptNext,
  Marker,
} from '@react-google-maps/api';

import {ENV} from '@/constants/EnvConstants';
import useWindowSize from '@/hooks/WindowSize/WindowSize';
import logError from '@/utils/errors/logError';

interface GeoMapsProps {
  line1: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

const libraries: Libraries = ['marker'];

const GeoMap: React.FC<GeoMapsProps> = ({
  line1,
  city,
  state,
  postal_code,
  country,
}: GeoMapsProps) => {
  const [mapLatitude, setMapLatitude] = useState<number | null>(null);
  const [mapLongitude, setMapLongitude] = useState<number | null>(null);
  const [showMap, setShowMap] = useState<boolean>(false);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(
    null,
  );
  const {isMobile} = useWindowSize();

  const getShippingAddressCoordinates = useCallback(async () => {
    const address = `${line1}, ${city}, ${state} ${postal_code} ${country}`;
    try {
      const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${ENV.VITE_GOOGLE_GEOCODE_KEY}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(
          `Google Geo Code API error: ${response.status} ${response.statusText}`,
        );
      }

      const data = await response.json();

      if (!data) {
        throw new Error(`Google Geo Code API did not return data`);
      }

      if (data.results && data.results.length > 0) {
        const location = data.results[0].geometry.location;
        setMapLatitude(location.lat);
        setMapLongitude(location.lng);
        setShowMap(true);
      }
    } catch (error) {
      logError(error, {
        customTitle: 'GeoMap - Error getting shipping address coordinates',
        fileName: 'GeoMap',
        functionName: 'getShippingAddressCoordinates',
        data: {
          address,
        },
      });
    }
  }, [city, country, line1, postal_code, state]);

  useEffect(() => {
    if (line1 && city && state && postal_code && country) {
      getShippingAddressCoordinates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [line1, city, state, postal_code, country]);

  useEffect(() => {
    if (showMap && mapLatitude && mapLongitude && mapRef.current) {
      const position = {lat: mapLatitude, lng: mapLongitude};
      if (markerRef.current) {
        markerRef.current.position = position;
      } else {
        markerRef.current = new google.maps.marker.AdvancedMarkerElement({
          map: mapRef.current,
          position: position,
        });
      }
    }
  }, [showMap, mapLatitude, mapLongitude]);

  return (
    <div>
      {showMap && mapLatitude && mapLongitude && (
        <LoadScriptNext
          googleMapsApiKey={ENV.VITE_GOOGLE_PLACES_KEY}
          libraries={libraries}>
          <div
            style={{
              borderRadius: '8px',
              overflow: 'hidden',
              width: '100%',
            }}>
            <GoogleMap
              mapContainerStyle={{
                width: isMobile ? '92vw' : '456px',
                height: '160px',
                overflow: 'hidden',
              }}
              center={{lat: mapLatitude, lng: mapLongitude}}
              zoom={17}
              onLoad={(map: google.maps.Map) => {
                mapRef.current = map;
              }}
              options={{
                keyboardShortcuts: false,
                gestureHandling: 'false',
                disableDefaultUI: true,
              }}>
              <Marker position={{lat: mapLatitude, lng: mapLongitude}} />
            </GoogleMap>
          </div>
        </LoadScriptNext>
      )}
    </div>
  );
};

export default GeoMap;
